import React, { useState, useMemo, useEffect } from 'react';
import { Receipt, Plus, Edit, Trash2, FileText, Search, Send, CheckCircle, ArrowRight, Mail, Printer, Wallet, QrCode, RotateCcw, Scissors, Download, Wrench, Star, Sparkles, Copy, X, Loader2 } from 'lucide-react';
import { useData } from '../context/DataContext';
import { carsysApi } from '../api/carsysApi';
import { toast } from 'sonner';
import BonEditor from '../components/BonEditor';
import PrintFactuur from '../components/PrintFactuur';
import BetaalQR from '../components/BetaalQR';
import WerkorderModal from '../components/WerkorderModal';
import ReviewModal from '../components/ReviewModal';

import { useT } from '../context/LanguageContext';
import { confirmDialog } from '../lib/confirm';

const statusColor = (s) => {
  if (s === 'Betaald' || s === 'BetaaldBank' || s === 'BetaaldContant') return 'green';
  if (s === 'DeelsBetaald') return 'amber';
  if (s === 'Gefactureerd' || s === 'Akkoord') return 'blue';
  if (s === 'Geannuleerd' || s === 'Creditfactuur') return 'red';
  return 'gray';
};



export default function Bonnen() {
const { t, lang } = useT();

  const eur = (n) =>
    (n || 0).toLocaleString(
      lang === 'en' ? 'en-GB' : 'nl-NL',
      {
        style: 'currency',
        currency: 'EUR'
      }
    );

  const datum = (d) =>
    new Date(d).toLocaleDateString(
      lang === 'en' ? 'en-GB' : 'nl-NL'
    );

  const { bonnen: list, klanten, gegevens, deleteBon, setBonStatus, convertBon, sendEmail, refreshAll } = useData();
  const [filter, setFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('Alle');
  const [selectedId, setSelectedId] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorBon, setEditorBon] = useState(null);
  const [printBon, setPrintBon] = useState(null);
  const [qrBon, setQrBon] = useState(null);
  const [reviewBon, setReviewBon] = useState(null);
  const [aiModal, setAiModal] = useState(null); // { bon, text, loading }

  const genereerAiSamenvatting = async (bon) => {
    setAiModal({ bon, text: '', loading: true });
    try {
      const res = await carsysApi.werkorderAiSamenvatting(bon.id);
      setAiModal({ bon, text: res.samenvatting || '', loading: false });
    } catch (e) {
      setAiModal(null);
      toast.error(t('AI-samenvatting mislukt', 'AI-samenvatting mislukt'), { description: e.response?.data?.detail || e.message });
    }
  };
  const [werkorderBon, setWerkorderBon] = useState(null);
  const [deelOpen, setDeelOpen] = useState(false);
  const [deelBedrag, setDeelBedrag] = useState('');
  const [voorschotten, setVoorschotten] = useState([]);

  useEffect(() => {
    carsysApi.getVoorschotten().then(setVoorschotten).catch(() => {});
  }, [list]); // re-fetch wanneer bonnen wijzigen (na save sync nieuwe voorschotten)

  // Map bonId → open voorschot (alleen status='open')
  const voorschotByBon = useMemo(() => {
    const m = {};
    for (const v of voorschotten) {
      if (v.bonId && v.status === 'open') m[v.bonId] = v;
    }
    return m;
  }, [voorschotten]);

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return list.filter(b => {
      const klant = klanten.find(k => k.id === b.klantId);
      const ok = !f || b.nummer.toLowerCase().includes(f) || (klant?.naam || '').toLowerCase().includes(f) || b.omschrijving.toLowerCase().includes(f) || b.kenteken.toLowerCase().includes(f);
      const t = typeFilter === 'Alle' || b.type === typeFilter;
      return ok && t;
    });
  }, [list, filter, typeFilter, klanten]);

  const selected = list.find(b => b.id === selectedId) || filtered[0] || list[0];
  const selectedKlant = selected ? klanten.find(k => k.id === selected.klantId) : null;

  const handleNew = () => { setEditorBon(null); setEditorOpen(true); };
  const handleEdit = () => { if (!selected) return; setEditorBon(selected); setEditorOpen(true); };
  const handleDelete = async () => {
    if (!selected) return;
    if (!(await confirmDialog(`${t('Bon verwijderen?')} — ${selected.nummer}`))) return;
    try { await deleteBon(selected.id); toast.success(t('Bon verwijderd')); setSelectedId(null); }
    catch { toast.error(t('Verwijderen mislukt')); }
  };
  const handlePdf = () => { if (selected) setPrintBon(selected); };
  const handleStatus = async (status) => {
    if (!selected) return;
    try { await setBonStatus(selected.id, { status }); toast.success(`Status: ${status}`); }
    catch { toast.error(t('Status update mislukt')); }
  };
  const handleConvert = async (target) => {
    if (!selected) return;
    try {
      const nieuwe = await convertBon(selected.id, target);
      toast.success(`${t('Omgezet naar', 'Omgezet naar')} ${t(target, target)}: ${nieuwe.nummer}`);
      setSelectedId(nieuwe.id);
    } catch { toast.error(t('Omzetten mislukt')); }
  };
  const handleCredit = async () => {
    if (!selected || selected.type !== 'Factuur') return;
    if (!(await confirmDialog(`${t('Creditfactuur maken voor', 'Creditfactuur maken voor')} ${selected.nummer}?`))) return;
    try {
      const cf = await carsysApi.creditBon(selected.id);
      await refreshAll(true);
      toast.success(t('Creditfactuur aangemaakt', 'Creditfactuur aangemaakt'), { description: cf.nummer });
      setSelectedId(cf.id);
    } catch (e) { toast.error(t('Crediteren mislukt', 'Crediteren mislukt'), { description: e.response?.data?.detail || e.message }); }
  };
  const handleDeelfactuur = async () => {
    const bedrag = parseFloat(deelBedrag);
    if (!selected || !bedrag || bedrag <= 0) { toast.error(t('Vul een bedrag in', 'Vul een bedrag in')); return; }
    try {
      const df = await carsysApi.deelfactuurBon(selected.id, { bedrag, omschrijving: `${t('Deelfactuur', 'Deelfactuur')} ${selected.nummer}` });
      await refreshAll(true);
      toast.success(t('Deelfactuur aangemaakt', 'Deelfactuur aangemaakt'), { description: df.nummer });
      setDeelOpen(false); setDeelBedrag(''); setSelectedId(df.id);
    } catch (e) { toast.error(t('Deelfactuur mislukt', 'Deelfactuur mislukt'), { description: e.response?.data?.detail || e.message }); }
  };
  const handleExport = () => {
    const a = document.createElement('a');
    a.href = carsysApi.exportFacturenUrl();
    a.download = 'facturen.csv';
    document.body.appendChild(a); a.click(); a.remove();
    toast.success(t('Export gestart', 'Export gestart'));
  };

  const handleEmail = async () => {
    if (!selected || !selectedKlant) return;
    if (!selectedKlant.email) {
      toast.error(t('Klant heeft geen e-mailadres', 'Klant heeft geen e-mailadres'));
      return;
    }
    // Voeg een online-betaallink toe als online betalen aan staat en er openstaand bedrag is
    let betaalRegel = '';
    const openstaand = Number(selected.openstaand ?? selected.totaal) || 0;
    if (selected.type === 'Factuur' && openstaand > 0) {
      try {
        const link = await carsysApi.createBetaallink(selected.id);
        if (link?.url) betaalRegel = `\n\n${t('Betaal direct online (iDEAL/creditcard):', 'Betaal direct online (iDEAL/creditcard):')}\n${link.url}`;
      } catch (e) { /* online betalen uit of geen factuur — link gewoon weglaten */ }
    }
    try {
      const res = await sendEmail({
        aan: selectedKlant.email,
        onderwerp: `${selected.type} ${selected.nummer}`,
        body: `Beste ${selectedKlant.naam},\n\nHierbij ${selected.type.toLowerCase()} ${selected.nummer} met totaal ${eur(selected.totaal)}.${betaalRegel}\n\nMet vriendelijke groet,\nMonteurCentraal`,
        type: 'bon',
      });
      const isReal = !String(res.status || '').toLowerCase().startsWith('mock');
      if (isReal) toast.success(`${t('E-mail verstuurd', 'E-mail verstuurd')}: ${res.status}`);
      else toast.warning(t('E-mail (mock)', 'E-mail (mock)'), { description: res.status });
    } catch (e) { toast.error(t('E-mail mislukt'), { description: e.response?.data?.detail || e.message }); }
  };

  return (
    <div>
      <div className="cs-page-header">
        <div><h2>{t('page.receipts.title')}</h2><p>{t('page.receipts.sub')}</p></div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="cs-btn primary" onClick={handleNew}><Plus size={14} /> {t('Nieuwe bon')}</button>
          <button className="cs-btn" onClick={handleEdit} disabled={!selected}><Edit size={14} /> {t('Openen','Openen')}</button>
          <button className="cs-btn" onClick={handlePdf} disabled={!selected}><Printer size={14} /> {t('PDF','PDF')}</button>
          <button className="cs-btn" onClick={() => selected && setWerkorderBon(selected)} disabled={!selected} data-testid="bon-werkorder"><Wrench size={14} /> {t('Werkorder', 'Werkorder')}</button>
          <button className="cs-btn" onClick={() => selected && setQrBon(selected)} disabled={!selected || (selected.type !== 'Factuur' && selected.type !== 'Creditfactuur')} data-testid="bon-betaal-qr"><QrCode size={14} /> {t('Betaal-QR', 'Betaal-QR')}</button>
          <button className="cs-btn" onClick={() => selected && setReviewBon(selected)} disabled={!selected || selected.type !== 'Factuur'} data-testid="bon-review"><Star size={14} /> {t('Vraag review', 'Vraag review')}</button>
          <button className="cs-btn" onClick={() => selected && genereerAiSamenvatting(selected)} disabled={!selected || !(selected.regels || []).length} data-testid="bon-ai-samenvatting"><Sparkles size={14} /> {t('AI-samenvatting', 'AI-samenvatting')}</button>
          <button className="cs-btn" onClick={() => setDeelOpen(true)} disabled={!selected} data-testid="bon-deelfactuur"><Scissors size={14} /> {t('Deelfactuur', 'Deelfactuur')}</button>
          <button className="cs-btn" onClick={handleCredit} disabled={!selected || selected.type !== 'Factuur'} data-testid="bon-credit"><RotateCcw size={14} /> {t('Crediteren', 'Crediteren')}</button>
          <button className="cs-btn" onClick={handleExport} data-testid="bon-export"><Download size={14} /> {t('Export boekhouder', 'Export boekhouder')}</button>
          <button className="cs-btn" onClick={handleEmail} disabled={!selected}><Mail size={14} /> {t('Mail', 'Mail')}</button>
          <button className="cs-btn danger" onClick={handleDelete} disabled={!selected}><Trash2 size={14} /> {t('Verwijderen')}</button>
        </div>
      </div>

      <div className="cs-toolbar">
        <div className="grow" style={{ position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: 'var(--cs-text-muted)' }} />
          <input className="cs-input" style={{ paddingLeft: 30 }} placeholder={t('Zoek op nummer, klant, omschrijving, kenteken...', 'Zoek op nummer, klant, omschrijving, kenteken...')} value={filter} onChange={e => setFilter(e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 4, background: '#fff', border: '1px solid var(--cs-border)', borderRadius: 6, padding: 2 }}>
          {['Alle', 'Werkbon', 'Offerte', 'Factuur', 'Creditfactuur'].map(tp => (
            <button key={tp} className="cs-btn ghost" style={{ padding: '4px 12px', background: typeFilter === tp ? 'var(--cs-accent-soft)' : 'transparent', color: typeFilter === tp ? 'var(--cs-accent)' : 'inherit' }} onClick={() => setTypeFilter(tp)}>{t(tp, tp)}</button>
          ))}
        </div>
      </div>

      <div className="cs-master-detail">
        <div className="cs-card">
          <table className="cs-table">
            <thead><tr><th>{t('Nummer')}</th><th>{t('Datum')}</th><th>{t('Klant')}</th><th>{t('Type')}</th><th>{t('Status')}</th><th style={{textAlign:'right'}}>{t('Totaal')}</th><th style={{textAlign:'right'}}>{t('Open')}</th></tr></thead>
            <tbody>
              {filtered.map(b => {
                const klant = klanten.find(k => k.id === b.klantId);
                const vs = voorschotByBon[b.id];
                return (
                  <tr key={b.id} className={b.id === selected?.id ? 'selected' : ''} onClick={() => setSelectedId(b.id)} onDoubleClick={() => { setEditorBon(b); setEditorOpen(true); }}>
                    <td data-label={t('Bonnummer')} style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 600 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        {b.nummer}
                        {vs && (
                          <span
                            title={`${t('Open voorschot', 'Open voorschot')}: ${eur(vs.bedrag)} — ${vs.userNaam || vs.userEmail}`}
                            data-testid={`bon-voorschot-icon-${b.id}`}
                            style={{ display: 'inline-flex' }}
                          >
                            <Wallet size={13} color="#f59e0b" aria-label={t("Open voorschot","Open voorschot")} />
                          </span>
                        )}
                      </span>
                    </td>
                    <td data-label={t('Datum')}>{datum(b.datum)}</td>
                    <td data-label={t('Klant')}>{klant?.naam || '-'}</td>
                    <td data-label={t('Type')}><span className={`cs-badge ${b.type === 'Factuur' ? 'blue' : b.type === 'Offerte' ? 'amber' : 'gray'}`}>{t(b.type,b.type)}</span></td>
                    <td data-label={t('Status')}><span className={`cs-badge ${statusColor(b.status)}`}>{t(b.status,b.status)}</span></td>
                    <td data-label={t('Totaal')} style={{ textAlign: 'right', fontWeight: 600 }}>{eur(b.totaal)}</td>
                    <td data-label={t('Openstaand')} style={{ textAlign: 'right', color: b.openstaand > 0 ? 'var(--cs-red)' : 'var(--cs-text-muted)' }}>{b.openstaand > 0 ? eur(b.openstaand) : '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && <div className="cs-empty">{t('Geen bonnen gevonden.', 'Geen bonnen gevonden.')}</div>}
        </div>

        <div className="cs-card padded">
          {selected ? (
            <>
              <h3 className="cs-card-title"><Receipt size={16} /> {selected.nummer}</h3>
              <div className="cs-detail-row"><span className="lbl">{t('Type')}</span><span className="val">{t(selected.type,selected.type)}</span></div>
              <div className="cs-detail-row"><span className="lbl">{t('Status')}</span><span className="val"><span className={`cs-badge ${statusColor(selected.status)}`}>{t(selected.status,selected.status)}</span></span></div>
              <div className="cs-detail-row"><span className="lbl">{t('Werkstatus')}</span><span className="val">{t(selected.werkStatus,selected.werkStatus)}</span></div>
              <div className="cs-detail-row"><span className="lbl">{t('Datum')}</span><span className="val">{datum(selected.datum)}</span></div>
              <div className="cs-detail-row"><span className="lbl">{t('Klant')}</span><span className="val">{selectedKlant?.naam || '-'}</span></div>
              <div className="cs-detail-row"><span className="lbl">{t('Kenteken')}</span><span className="val" style={{ fontFamily: 'ui-monospace, monospace' }}>{selected.kenteken}</span></div>
              <div className="cs-detail-row"><span className="lbl">{t('Omschrijving')}</span><span className="val">{selected.omschrijving}</span></div>
              <div className="cs-detail-row"><span className="lbl">{t('Totaal')}</span><span className="val" style={{ fontWeight: 700 }}>{eur(selected.totaal)}</span></div>
              <div className="cs-detail-row"><span className="lbl">{t('Betaald')}</span><span className="val" style={{ color: 'var(--cs-green)' }}>{eur(selected.betaald)}</span></div>
              <div className="cs-detail-row"><span className="lbl">{t('Openstaand')}</span><span className="val" style={{ color: selected.openstaand > 0 ? 'var(--cs-red)' : 'inherit', fontWeight: 600 }}>{eur(selected.openstaand)}</span></div>
              {voorschotByBon[selected.id] && (
                <div className="cs-detail-row" data-testid="bon-voorschot-detail">
                  <span className="lbl"><Wallet size={12} style={{ verticalAlign: 'middle', color: '#f59e0b' }} /> {t('Open voorschot','Open voorschot')}</span>
                  <span className="val" style={{ color: '#b45309', fontWeight: 600 }}>
                    {eur(voorschotByBon[selected.id].bedrag)}
                    <span style={{ color: 'var(--cs-text-muted)', fontWeight: 400, marginLeft: 6, fontSize: 12 }}>
                      {t('door', 'door')} {voorschotByBon[selected.id].userNaam || voorschotByBon[selected.id].userEmail}
                    </span>
                  </span>
                </div>
              )}

              {/* Workflow buttons */}
              <h4 style={{ margin: '14px 0 6px', fontSize: 12, color: 'var(--cs-text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{t('Workflow', 'Workflow')}</h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {selected.type === 'Offerte' && <>
                  <button className="cs-btn ghost" style={{ padding: '4px 10px' }} onClick={() => handleStatus('OfferteVerstuurd')}><Send size={11} /> {t('Verstuurd', 'Verstuurd')}</button>
                  <button className="cs-btn ghost" style={{ padding: '4px 10px' }} onClick={() => handleStatus('Akkoord')}><CheckCircle size={11} /> {t('Akkoord', 'Akkoord')}</button>
                  <button className="cs-btn ghost" style={{ padding: '4px 10px' }} onClick={() => handleConvert('Werkbon')}><ArrowRight size={11} /> → {t('Werkbon', 'Werkbon')}</button>
                  <button className="cs-btn ghost" style={{ padding: '4px 10px' }} onClick={() => handleConvert('Factuur')}><ArrowRight size={11} /> → {t('Factuur', 'Factuur')}</button>
                </>}
                {selected.type === 'Werkbon' && <>
                  <button className="cs-btn ghost" style={{ padding: '4px 10px' }} onClick={() => handleStatus('InUitvoering')}>{t('In uitvoering')}</button>
                  <button className="cs-btn ghost" style={{ padding: '4px 10px' }} onClick={() => handleStatus('WachtenOpParts')}>{t('Wacht op onderdelen', 'Wacht op onderdelen')}</button>
                  <button className="cs-btn ghost" style={{ padding: '4px 10px' }} onClick={() => handleConvert('Factuur')}><ArrowRight size={11} /> → {t('Factuur', 'Factuur')}</button>
                </>}
                {selected.type === 'Factuur' && <>
                  <button className="cs-btn ghost" style={{ padding: '4px 10px' }} onClick={() => handleStatus('Gefactureerd')}>{t('Gefactureerd','Gefactureerd')}</button>
                  <button className="cs-btn ghost" style={{ padding: '4px 10px' }} onClick={() => handleStatus('Betaald')}>{t('Betaald','Betaald' )}</button>
                </>}
              </div>

              {selected.regels && selected.regels.length > 0 && (
                <>
                  <h4 style={{ margin: '14px 0 6px', fontSize: 13 }}>{t('Regels','Regels' )}</h4>
                  <table className="cs-table">
                    <thead><tr><th>{t('Omschrijving')}</th><th style={{textAlign:'right'}}>{t('Aantal')}</th><th style={{textAlign:'right'}}>{t('Prijs')}</th></tr></thead>
                    <tbody>
                      {selected.regels.map((r, i) => (
                        <tr key={i}>
                          <td>{r.omschr} <span className="cs-badge gray" style={{ marginLeft: 6 }}>{t(r.type,r.type)}</span></td>
                          <td style={{ textAlign: 'right' }}>{r.aantal}</td>
                          <td style={{ textAlign: 'right' }}>{eur(r.prijs)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </>
          ) : <div className="cs-empty">{t('Selecteer een bon.', 'Selecteer een bon.')}</div>}
        </div>
      </div>

      {editorOpen && <BonEditor bon={editorBon} onClose={() => setEditorOpen(false)} />}
      {printBon && <PrintFactuur bon={printBon} klant={klanten.find(k => k.id === printBon.klantId)} gegevens={gegevens} onClose={() => setPrintBon(null)} />}
      {qrBon && <BetaalQR bon={qrBon} onClose={() => setQrBon(null)} />}
      {reviewBon && <ReviewModal bon={reviewBon} onClose={() => setReviewBon(null)} />}

      {aiModal && (
        <div className="cs-modal-overlay" onClick={() => setAiModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div className="cs-modal" onClick={e => e.stopPropagation()} style={{ width: 540, maxWidth: '94vw', background: '#fff', borderRadius: 10, padding: 22 }} data-testid="ai-samenvatting-modal">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <h3 style={{ margin: 0 }}><Sparkles size={16} style={{ verticalAlign: -2, marginRight: 6, color: '#8b5cf6' }} />{t('AI-werkordersamenvatting', 'AI-werkordersamenvatting')}</h3>
              <button className="cs-btn ghost" onClick={() => setAiModal(null)} data-testid="ai-close"><X size={16} /></button>
            </div>
            <p style={{ fontSize: 12.5, color: 'var(--cs-text-muted)', marginTop: 0 }}>{t('Klantvriendelijke uitleg van het uitgevoerde werk — handig voor de factuur-e-mail of een appje.', 'Klantvriendelijke uitleg van het uitgevoerde werk — handig voor de factuur-e-mail of een appje.')}</p>
            {aiModal.loading ? (
              <div className="cs-empty" style={{ padding: 28 }} data-testid="ai-loading"><Loader2 size={18} className="animate-spin" style={{ verticalAlign: -3, marginRight: 8 }} />{t('AI schrijft de samenvatting…', 'AI schrijft de samenvatting…')}</div>
            ) : (
              <textarea className="cs-input" data-testid="ai-samenvatting-text" style={{ width: '100%', minHeight: 150, resize: 'vertical', lineHeight: 1.5 }} value={aiModal.text} onChange={e => setAiModal({ ...aiModal, text: e.target.value })} />
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
              <button className="cs-btn" onClick={() => setAiModal(null)}>{t('Sluiten', 'Sluiten')}</button>
              <button className="cs-btn primary" data-testid="ai-copy" disabled={aiModal.loading || !aiModal.text} onClick={() => { navigator.clipboard?.writeText(aiModal.text); toast.success(t('Gekopieerd', 'Gekopieerd')); }}><Copy size={14} /> {t('Kopieer tekst', 'Kopieer tekst')}</button>
            </div>
          </div>
        </div>
      )}
      {werkorderBon && <WerkorderModal bon={werkorderBon} onClose={() => setWerkorderBon(null)} />}
      {deelOpen && selected && (
        <div className="cs-modal-overlay" onClick={() => setDeelOpen(false)}>
          <div className="cs-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 380 }} data-testid="deelfactuur-modal">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}><Scissors size={16} style={{ verticalAlign: 'middle' }} /> {t('Deelfactuur', 'Deelfactuur')}</h3>
              <button className="cs-btn ghost" onClick={() => setDeelOpen(false)}><Trash2 style={{ display: 'none' }} />×</button>
            </div>
            <p style={{ fontSize: 13, color: 'var(--cs-text-muted)', marginTop: 0 }}>
              {t('Maak een deelfactuur (termijn/aanbetaling) voor', 'Maak een deelfactuur (termijn/aanbetaling) voor')} {selected.nummer}. {t('Bedrag is exclusief BTW.', 'Bedrag is exclusief BTW.')}
            </p>
            <label className="cs-section-title">{t('Bedrag (excl. BTW)', 'Bedrag (excl. BTW)')}</label>
            <input type="number" step="0.01" className="cs-input" value={deelBedrag} onChange={e => setDeelBedrag(e.target.value)} placeholder="0,00" autoFocus data-testid="deelfactuur-bedrag" />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
              <button className="cs-btn" onClick={() => setDeelOpen(false)}>{t('Annuleren')}</button>
              <button className="cs-btn primary" onClick={handleDeelfactuur} data-testid="deelfactuur-maak">{t('Deelfactuur maken', 'Deelfactuur maken')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
